import React from 'react';
import { MessageCircle } from 'lucide-react';
import {
  ChatBubble as ChatBubbleUI,
  useChat,
  ChatConfig,
} from '@clickster/ui-library/dist/chat';
import { chatAdapter } from '@/src/adapters/chat-adapter';

const defaultConfig: ChatConfig = {
  webhookUrl: 'https://n8n.clickster.io/webhook/066b342b-f2b6-401e-b560-12f5d23b6103/chat',
  platformHelpWebhookUrl: 'https://n8n.clickster.io/webhook/066b342b-f2b6-401e-b560-12f5d23b6103/chat',
  chatInputKey: 'chatInput',
  chatSessionKey: 'sessionId',
  title: 'Clickster AI Assistant',
  subtitle: 'Ask me anything about your campaigns',
  placeholder: 'Type your message...',
};

export interface ChatBubbleProps {
  webhookUrl?: string;
  platformHelpWebhookUrl?: string;
  className?: string;
  title?: string;
  subtitle?: string;
  placeholder?: string;
  getMetadata?: () => Record<string, any>;
}

export function ChatBubble({
  webhookUrl,
  platformHelpWebhookUrl,
  title,
  subtitle,
  placeholder,
  getMetadata,
}: ChatBubbleProps) {
  const config: ChatConfig = {
    ...defaultConfig,
    ...(webhookUrl && { webhookUrl }),
    ...(platformHelpWebhookUrl && { platformHelpWebhookUrl }),
    ...(title && { title }),
    ...(subtitle && { subtitle }),
    ...(placeholder && { placeholder }),
    ...(getMetadata && { getMetadata }),
  };

  const chat = useChat({
    apiClient: chatAdapter,
    ...config,
  });

  return (
    <div className="fixed flex flex-col items-end space-y-4 z-[101] bottom-0 right-0">
      {!chat.isOpen && (
        <button
          onClick={chat.toggleChat}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg transition-all duration-200 hover:scale-110"
          data-testid="chat-bubble-trigger-button"
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      <ChatBubbleUI
        isOpen={chat.isOpen}
        messages={chat.messages}
        isLoading={chat.isLoading}
        selectedAgent={chat.selectedAgent ?? undefined}
        title={title}
        subtitle={subtitle}
        placeholder={placeholder}
        currentSessionId={chat.sessionId}
        availableChats={chat.availableChats}
        isSwitchingChat={chat.isSwitchingChat}
        deletingChatId={chat.deletingChatId}
        streamingNodes={chat.streamingNodes}
        onClose={chat.closeChat}
        onSendMessage={chat.sendMessage}
        onSelectAgent={chat.selectAgent}
        onRestart={chat.restartChat}
        onSwitchChat={chat.switchChat}
        onDeleteChat={chat.deleteChat}
        onNewChat={chat.restartChat}
      />
    </div>
  );
}
